// src/services/subscriptionService.js
import axios from "axios";
import { BACKEND_URL } from "../utils/envConfig";
import subscriptionHistoryService from "./subscriptionHistoryService";

const API_URL = `${BACKEND_URL}/subscriptions`;

/**
 * Helper function to get allowed roles for package
 * Now all packages allow all roles - limitation is only on number of users
 */
const getRolesForPackage = (subType) => {
  // All packages now allow all roles - no role restrictions
  // The limitation is only on the total number of users allowed
  return []; // Return empty array to indicate all roles are allowed
};

// Subscription Packages
export const getSubscriptionPackages = async () => {
  try {
    // Try to get subscription types from new endpoint
    const subscriptionTypes = await subscriptionHistoryService.getSubscriptionTypes();
    
    // Transform to match existing package format for backward compatibility
    const packages = subscriptionTypes.map((type) => {
      // Handle capabilities - can be JSON string or array
      let capabilities = [];
      if (typeof type.capabilities === 'string') {
        try {
          capabilities = JSON.parse(type.capabilities);
        } catch (e) {
          capabilities = [];
        }
      } else if (Array.isArray(type.capabilities)) {
        capabilities = type.capabilities;
      }
      
      return {
        id: type.subType.toLowerCase(),
        name: type.subType,
        price: parseFloat(type.packageAmount),
        features: capabilities,
        maxUsers: type.maxUsersAllowed === -1 ? -1 : type.maxUsersAllowed,
        allowedRoles: [], // Empty array means all roles are allowed
        subscriptionTypeId: type.id,
      };
    });
    
    return { packages };
  } catch (error) {
    console.warn("New subscription types endpoint failed, falling back to old endpoint:", error);
    // Fallback to existing endpoint if new one fails
    try {
      const response = await axios.get(`${API_URL}/packages`);
      return response.data;
    } catch (fallbackError) {
      console.error("Both subscription package endpoints failed:", fallbackError);
      throw fallbackError;
    }
  }
};

// Create subscription
export const createSubscription = async (subscriptionData) => {
  const response = await axios.post(`${API_URL}`, subscriptionData);
  return response.data;
};

// Get subscription by organization ID
export const getSubscriptionByOrganizationId = async (organizationId) => {
  const response = await axios.get(`${API_URL}/organization/${organizationId}`);
  return response.data;
};

// Update subscription
export const updateSubscription = async (subscriptionId, subscriptionData) => {
  const response = await axios.put(`${API_URL}/${subscriptionId}`, subscriptionData);
  return response.data;
};

// Process payment
export const processPayment = async (paymentData) => {
  const response = await axios.post(`${API_URL}/payment`, paymentData);
  return response.data;
};

// Initialize organization database tables
export const initializeOrganizationDatabase = async (organizationId) => {
  const response = await axios.post(`${API_URL}/initialize-database/${organizationId}`);
  return response.data;
};

// Create missing tables for an existing organization
// This endpoint creates only the tables that don't exist, without affecting existing ones
export const createMissingTables = async (organizationId) => {
  try {
    const response = await axios.post(`${API_URL}/create-missing-tables/${organizationId}`);
    return response.data;
  } catch (error) {
    console.error("Error creating missing tables:", error);
    throw error;
  }
};

export default {
  getSubscriptionPackages,
  createSubscription,
  getSubscriptionByOrganizationId,
  updateSubscription,
  processPayment,
  initializeOrganizationDatabase,
  createMissingTables
};

